import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getCategories, getProducts, type Category } from '../lib/api';
import type { Product } from '../types';
import { useCart } from '../state/cart.store';

const CategoryPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { totalQuantity } = useCart();
    const [category, setCategory] = useState<Category | undefined>();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const [prods, cats] = await Promise.all([getProducts(), getCategories()]);
                if (!cancelled) {
                    setProducts(prods);
                    setCategory(cats.find(c => c.slug === slug));
                }
            } catch {
                if (!cancelled) setError('Impossibile caricare i prodotti');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [slug]);

    const categoryProducts = useMemo(() => {
        return products.filter(p => p.categorySlug === slug);
    }, [products, slug]);

    const productGroups = useMemo(() => {
        const byGroup = new Map<string, Product[]>();
        for (const product of categoryProducts) {
            const key = product.groupSlug ?? String(product.id);
            const arr = byGroup.get(key);
            if (arr) arr.push(product);
            else byGroup.set(key, [product]);
        }

        return Array.from(byGroup.entries())
            .map(([groupSlug, variants]) => {
                const first = variants[0];
                const prices = variants.map(v => v.price);
                const minPrice = Math.min(...prices);
                const maxPrice = Math.max(...prices);
                return {
                    groupSlug,
                    name: first?.name ?? 'Prodotto',
                    description: first?.description,
                    imageUrl: first?.imageUrl,
                    videoUrl: first?.videoUrl,
                    variants,
                    minPrice,
                    maxPrice,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [categoryProducts]);

    return (
        <div className="category-page">
            <p>
                <Link to="/">← Tutte le categorie</Link>
            </p>

            <div className="menu-header">
                <h2>{category?.title ?? 'Categoria'}</h2>
                <Link to="/cart" className="cart-badge-link">
                    Carrello {totalQuantity > 0 && <span className="cart-badge">{totalQuantity}</span>}
                </Link>
            </div>

            {loading && <p>Caricamento...</p>}
            {error && <p className="error">{error}</p>}
            {!loading && !error && (
                <div className="product-list">
                    {productGroups.map(group => (
                        <Link
                            key={group.groupSlug}
                            to={`/product/${group.groupSlug}`}
                            className="product-card product-card-link"
                        >
                            {group.videoUrl ? (
                                <video
                                    className="card-video"
                                    src={group.videoUrl}
                                    muted
                                    autoPlay
                                    loop
                                    playsInline
                                    preload="metadata"
                                    poster={group.imageUrl || undefined}
                                />
                            ) : group.imageUrl ? (
                                <img src={group.imageUrl} alt={group.name} />
                            ) : (
                                <div className="image-placeholder" aria-hidden="true" />
                            )}
                            <h3>{group.name}</h3>
                            {group.description ? <p className="card-desc">{group.description}</p> : null}
                            <p className="price-range">
                                {group.minPrice === group.maxPrice
                                    ? `€${group.minPrice.toFixed(2)}`
                                    : `da €${group.minPrice.toFixed(2)} a €${group.maxPrice.toFixed(2)}`}
                            </p>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CategoryPage;
